import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import {
  PRIVACY_NOTICE_TITLE,
  PRIVACY_NOTICE_INTRO,
  DATA_CATEGORIES,
  WHO_CAN_SEE,
  CHILDREN_AND_GUARDIANS,
  WHAT_LEAVES,
  DELETION_AND_CONTACT,
  COMING_LATER,
} from "../lib/privacyNotice";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { BrandMark } from "../components/brand/BrandMark";

/**
 * PrivacyNoticeNew — issue #974 (D186.11/D186.12), `/privacy` redrawn to
 * `Privacy.dc.html` (design-baseline-round-5, sha256
 * 226d5367a1b6d05ee98aac65b15181d8c48bbd5632497167f70abebfda2a7e6b), behind
 * its OWN `newUi("privacy")` flag with NO chain — see `pages/
 * PrivacyNotice.jsx`'s own early return for the gate.
 *
 * PRESENTATION ONLY. Every sentence on this page is read from
 * `lib/privacyNotice.js` — all eight of its exports, imported above — and
 * NONE of the notice's words are written inline here (D186.11: the notice's
 * published words, issue numbers included, stay verbatim, and change only
 * through the privacy-notice wording route, e.g. PR #861). One static
 * state; nothing on this page loads, fails or changes.
 *
 * CHROME. The drawing draws its own header (mark, "Compass") outside the
 * app shell — no TopBar, no accessibility menu, chromeless like the
 * getting-in screens (`ForgotPasswordNew.jsx`, `SignInNew.jsx`, …): "a
 * parent should be able to read it before an account exists." `BrandMark`
 * (`components/brand/BrandMark.jsx`, #1300) is the one mark every new-look
 * screen draws; this page used to carry its own copy of the SVG.
 *
 * The amber "Interim notice" badge is drawn navy-on-tint here (no amber in
 * the `--nw-*` tokens), and there is no gold anywhere on this page — it has
 * no action to take.
 */
const sectionHeadingStyle = {
  margin: 0,
  fontFamily: "'Manrope', sans-serif",
  fontWeight: 800,
  fontSize: 18,
  color: "var(--nw-ink)",
};

const bodyTextStyle = {
  margin: 0,
  fontSize: 13,
  lineHeight: 1.55,
  color: "var(--nw-ink-lede)",
};

/**
 * #1125 — where Back goes. The notice is opened from Settings, the sign-up
 * form and the dictation explainer; it used to return to `/` from all of
 * them. The origin arrives as router state (`state.from`, a path string)
 * or, for the dictation explainer's plain `<a>`, as `?from=`. Both are data
 * the app was handed, so both go through `safeRedirectTarget` (same-origin
 * app paths only; `//`, schemes and backslashes fall back to `/`).
 * `/register` is public, so it is named as an extra path rather than added
 * to the sign-in allowlist.
 */
const BACK_EXTRA_PATHS = ["/register"];
function backTarget(location) {
  const fromState = location?.state?.from;
  const fromQuery = new URLSearchParams(location?.search || "").get("from");
  return safeRedirectTarget(fromState ?? fromQuery, "/", { extraPaths: BACK_EXTRA_PATHS });
}
function backLabel(target) {
  if (/^\/settings([/?#]|$)/.test(target)) return "Back to Settings";
  if (/^\/register([?#]|$)/.test(target)) return "Back to sign up";
  return "Back to CricketCompass";
}

export default function PrivacyNoticeNew() {
  const location = useLocation();
  const target = backTarget(location);
  return (
    <div style={{ minHeight: "100vh", background: "var(--nw-page)" }}>
      <span data-testid="privacy-notice-new-screen" className="sr-only" style={{ color: "var(--nw-ink)" }} aria-hidden="true" />

      <div
        data-testid="privacy-notice-page"
        className="nw-column"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 18,
          padding: "24px 20px",
          color: "var(--nw-ink)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BrandMark size={24} />
          <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: 16, color: "var(--nw-navy)" }}>
            Compass
          </span>
        </div>

        <div>
          <span
            data-testid="privacy-notice-interim-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 10px",
              background: "var(--nw-navy-tint)",
              color: "var(--nw-navy)",
              fontSize: 11,
              fontWeight: 700,
              borderRadius: "var(--nw-radius-pill)",
              whiteSpace: "nowrap",
            }}
          >
            Interim notice
          </span>
        </div>

        <h1 className="nw-title" style={{ margin: 0 }}>
          {PRIVACY_NOTICE_TITLE}
        </h1>

        {PRIVACY_NOTICE_INTRO.map((paragraph) => (
          <p key={paragraph} style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: "var(--nw-ink-lede)" }}>
            {paragraph}
          </p>
        ))}

        <section data-testid="privacy-notice-categories" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h2 style={sectionHeadingStyle}>What we hold</h2>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {DATA_CATEGORIES.map((category) => (
              <div key={category.id} id={category.id} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--nw-ink)" }}>{category.label}</div>
                <div style={bodyTextStyle}>{category.detail}</div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="privacy-notice-who-can-see" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h2 style={sectionHeadingStyle}>Who can see what</h2>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55, color: "var(--nw-ink-lede)" }}>
            {WHO_CAN_SEE.map((paragraph) => (
              <li key={paragraph} style={{ marginBottom: 8 }}>
                {paragraph}
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="privacy-notice-children" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 style={sectionHeadingStyle}>Children and guardians</h2>
          {CHILDREN_AND_GUARDIANS.map((paragraph) => (
            <p key={paragraph} style={bodyTextStyle}>
              {paragraph}
            </p>
          ))}
        </section>

        <section data-testid="privacy-notice-what-leaves" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <h2 style={sectionHeadingStyle}>What leaves our systems</h2>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.55, color: "var(--nw-ink-lede)" }}>
            {WHAT_LEAVES.map((paragraph) => (
              <li key={paragraph} style={{ marginBottom: 8 }}>
                {paragraph}
              </li>
            ))}
          </ul>
        </section>

        <section data-testid="privacy-notice-deletion" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <h2 style={sectionHeadingStyle}>Deletion, and how to ask us about your data</h2>
          {DELETION_AND_CONTACT.map((paragraph) => (
            <p key={paragraph} style={bodyTextStyle}>
              {paragraph}
            </p>
          ))}
        </section>

        <div
          data-testid="privacy-notice-coming-later"
          style={{
            padding: 14,
            background: "var(--nw-line-faint)",
            border: "1px solid var(--nw-line)",
            borderRadius: "var(--nw-radius-card)",
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--nw-ink-lede)",
          }}
        >
          {COMING_LATER}
        </div>

        <Link
          to={target}
          data-testid="privacy-notice-back"
          style={{
            alignSelf: "flex-start",
            display: "inline-flex",
            alignItems: "center",
            minHeight: 44, // G4: a 44px tap target
            gap: 6,
            fontSize: 14,
            fontWeight: 600,
            color: "var(--nw-navy)",
            textDecoration: "none",
          }}
        >
          <ArrowLeft size={14} /> {backLabel(target)}
        </Link>
      </div>
    </div>
  );
}
